import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { checkIsDemoOrg } from '../models/organizations';
export enum QueryType {
  All = 'all',
  Filtered = 'filtered',
}

@Injectable()
export class AnalyticsService {
  private http = inject(HttpClient);

  private buildUrl(endpoint: string, organizationId: number, params: any = {}) {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    let url = `${environment.apiUrl}${demostr}/v1/analytics/stats/${endpoint}/${organizationId}/`;
    let httpParams = new HttpParams();
    for (const key in params) {
      if (params.hasOwnProperty(key) && params[key] !== null) {
        httpParams = httpParams.set(key, params[key]);
      }
    }

    return { url, params: httpParams };
  }

  getMttd(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('mttd', organizationId, params);
    return this.http.get<WidgetData>(url, { params: httpParams });
  }

  getMttrAverage(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('mttraverage', organizationId, params);
    return this.http.get<WidgetData>(url, { params: httpParams });
  }

  getTpr(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('truepositiverate', organizationId, params);
    return this.http.get<WidgetData>(url, { params: httpParams });
  }

  getDestinationMap(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null
  ): Observable<MapData[]> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/stats/destinationmap/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Add security_tool parameter if provided
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    return this.http.get<MapData[]>(baseUrl, { params });
  }

  getSourceMap(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null
  ): Observable<MapData[]> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/stats/sourcemap/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Add security_tool parameter if provided
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    return this.http.get<MapData[]>(baseUrl, { params });
  }

  getTopSuspectUsers(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null
  ): Observable<TopFiveData[]> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/stats/top5suspectuser/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Only add optional parameters if they have valid values
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    return this.http.get<TopFiveData[]>(baseUrl, { params });
  }

  getTopDetectionData(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    securityTool?: string | null,
    is_escalated?: boolean | null
  ): Observable<TopFiveData[]> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/stats/topdetections/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam);

    // Only add optional parameters if they have valid values
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    if (is_escalated !== null && is_escalated !== undefined) {
      params = params.set('is_escalated', is_escalated.toString());
    }

    return this.http.get<TopFiveData[]>(baseUrl, { params });
  }

  getTopUserDetail(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null,
    username?: string | null
  ): Observable<TopFiveData[]> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/stats/top5userdetail/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Only add optional parameters if they have valid values
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    // Support username filtering for drill-down
    if (username) {
      params = params.set('username', username);
    }

    return this.http.get<TopFiveData[]>(baseUrl, { params });
  }

  getTopSuspectAssets(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null
  ): Observable<TopFiveData[]> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';

    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/stats/top5suspectsourceip/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Only add optional parameters if they have valid values
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    return this.http.get<TopFiveData[]>(baseUrl, { params });
  }

  getTopAssetDetail(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    source_ip: string,
    type?: string,
    securityTool?: string | null
  ): Observable<TopFiveData[]> {
    const baseUrl = `${environment.apiUrl}/v1/analytics/stats/top5sourceipdetail/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('source_ip', source_ip)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Only add optional parameters if they have valid values
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    return this.http.get<TopFiveData[]>(baseUrl, { params });
  }

  getOutcomeStatus(organizationIds: number | number[], params?: any) {
    const orgId = Array.isArray(organizationIds) ? organizationIds[0] : organizationIds;
    const { url, params: httpParams } = this.buildUrl('truepositivevsfalsepositive', orgId, params);
    return this.http.get<TopFiveData[]>(url, { params: httpParams });
  }

  getOutcomeStatusMSP(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string
  ): Observable<TopFiveData> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/stats/truepositivevsfalsepositivemsp/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    const params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    return this.http.get<TopFiveData>(baseUrl, { params });
  }

  getTopUnknownRateCustomers(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string
  ): Observable<TopUnknownRateCustomer> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/stats/topunknownratecustomerschart/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    const params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    return this.http.get<TopUnknownRateCustomer>(baseUrl, { params });
  }

  getInProgressTickets(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('inprogresschart', organizationId, params);
    return this.http.get<WidgetData>(url, { params: httpParams });
  }

  getClosedTodayTickets(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('closedtodaychart', organizationId, params);
    return this.http.get<WidgetData>(url, { params: httpParams });
  }

  getNewTickets(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('newticketswidget', organizationId, params);
    return this.http.get<WidgetData>(url, { params: httpParams });
  }

  getTicketTrends(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('tickettrends', organizationId, params);
    return this.http.get<TrendingData>(url, { params: httpParams });
  }

  getFunnelData(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('socdistillationfunnel', organizationId, params);
    return this.http.get<FunnelData[]>(url, { params: httpParams });
  }

  getSlaViolations(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('slaviolations', organizationId, params);
    return this.http.get<WidgetData>(url, { params: httpParams });
  }

  getTotalTickets(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('numberoftickets', organizationId, params);
    return this.http.get<WidgetData>(url, { params: httpParams });
  }

  getDisposition(organizationIds: number | number[], params?: any) {
    const orgId = Array.isArray(organizationIds) ? organizationIds[0] : organizationIds;
    const { url, params: httpParams } = this.buildUrl('detectiondisposition', orgId, params);
    return this.http.get<DispositionData[]>(url, { params: httpParams });
  }

  getAdrManualData(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null
  ): Observable<AdrManualData[]> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/stats/adrmanual/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    return this.http.get<AdrManualData[]>(baseUrl, { params });
  }

  getTicketClosureBreakdownData(organizationIds: number | number[], params?: any) {
    const orgId = Array.isArray(organizationIds) ? organizationIds[0] : organizationIds;
    const { url, params: httpParams } = this.buildUrl('totalalertcases', orgId, params);
    return this.http.get<FunnelData[]>(url, { params: httpParams });
  }

  getMttrData(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('mttr', organizationId, params);
    return this.http.get<MttrData[]>(url, { params: httpParams });
  }

  getMttdData(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('mttdbypriority', organizationId, params);
    return this.http.get<MttdData[]>(url, { params: httpParams });
  }

  getConnectorStatusData(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('downconnects', organizationId, params);
    return this.http.get<ConnectorStatusData[]>(url, { params: httpParams });
  }

  getSlaAdherence(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('slaadherenceviewset', organizationId, params);
    return this.http.get<WidgetData>(url, { params: httpParams });
  }

  getOutcomeData(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('outcomerelevancydetails', organizationId, params);
    return this.http.get<WidgetData>(url, { params: httpParams });
  }

  getOpenThreats(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('edropenthreatswidget', organizationId, params);
    return this.http.get<WidgetData>(url, { params: httpParams });
  }

  getThreatRebootCount(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('edrthreatrebootsrequiredwidget', organizationId, params);
    return this.http.get<WidgetData>(url, { params: httpParams });
  }

  getAgentStatus(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('agentstatuschart', organizationId, params);
    return this.http.get<ChartData[]>(url, { params: httpParams });
  }

  getOutdatedAgents(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('edroutdatedagentswidget', organizationId, params);
    return this.http.get<WidgetData>(url, { params: httpParams });
  }

  getCoverage(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('edrcoveragewidget', organizationId, params);
    return this.http.get<CoverageData>(url, { params: httpParams });
  }

  getDetectedThreatStatus(organizationId: number, params?: any) {
    const { url, params: httpParams } = this.buildUrl('detectedthreatstatuschart', organizationId, params);
    return this.http.get<ChartData[]>(url, { params: httpParams });
  }

  getTicketTrendsAnalytics(
    organizationIds: number | number[],
    createdTimeAfter: string,
    createdTimeBefore: string,
    type?: string,
    securityTool?: string | null
  ): Observable<ticketTrendsResponse> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/administrative/tickettrends/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('created_time_after', createdTimeAfter)
      .set('created_time_before', createdTimeBefore)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Add security_tool parameter if provided
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    return this.http.get<ticketTrendsResponse>(baseUrl, { params });
  }

  getEventsVsEscalationsChartData(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null
  ): Observable<any> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/soc_v2/stats/eventvsescalations/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Add security_tool parameter if provided
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    return this.http.get<any>(baseUrl, { params }).pipe(
      map((response) => ({
        chart_data: response.chart || [],
        current_reduction_percentage: parseFloat(response.current_reduction_percentage) || 0,
        previous_reduction_percentage: parseFloat(response.previous_reduction_percentage) || 0,
        reduction_percentage_change: parseFloat(response.reduction_percentage_change) || 0,
      }))
    );
  }

  getAlertStatSummary(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null
  ): Observable<AlertStatSummaryResponse> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/administrative/alertstatsummary/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Add security_tool parameter if provided
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    return this.http.get<AlertStatSummaryResponse>(baseUrl, { params });
  }

  getSlaAdherenceV2(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null
  ): Observable<SlaAdherenceResponse> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/administrative/slaviolation/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    // Determine type based on organizationIds
    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Add security_tool parameter if provided
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    return this.http.get<SlaAdherenceResponse>(baseUrl, { params });
  }

  getPartnerMttd(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null
  ): Observable<PartnerMttdResponse> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/administrative/averagemttd/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Add security_tool parameter if provided
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    return this.http.get<PartnerMttdResponse>(baseUrl, { params });
  }

  getAverageMttr(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null
  ): Observable<AverageMttrResponse> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/administrative/averagemttr/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Add security_tool parameter if provided
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    return this.http.get<AverageMttrResponse>(baseUrl, { params });
  }

  getPartnerOrgCount(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null
  ): Observable<PartnerOrgCountResponse> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/administrative/partnerorgcount/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Add security_tool parameter if provided
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    return this.http.get<PartnerOrgCountResponse>(baseUrl, { params });
  }

  getDistillation(
    organizationId: number,
    createdTimeAfter: string,
    createdTimeBefore: string,
    type: string = 'filtered'
  ): Observable<SocEvent> {
    const baseUrl = `${environment.apiUrl}/v1/analytics/administrative/distillationfunneldetails/`;
    const params = new HttpParams()
      .set('created_time_after', createdTimeAfter)
      .set('created_time_before', createdTimeBefore)
      .set('org_ids', organizationId.toString())
      .set('type', type);

    return this.http.get<SocEvent>(baseUrl, { params });
  }

  getTicketsDispositionData(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null
  ): Observable<TicketsDispositionData[]> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/soc_v2/stats/ticketdispositions/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Add security_tool parameter if provided
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    return this.http.get<TicketsDispositionData[]>(baseUrl, { params });
  }

  getTopMttdDetectionsData(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null
  ) {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const url = `${environment.apiUrl}${demostr}/v1/analytics/soc_v2/stats/mttddetections/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Add security_tool parameter if provided
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }
    return this.http.get<TopMttdDetectionsData[]>(url, { params: params });
  }

  getCustomerDispositionData(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null
  ): Observable<CustomerDispositionData[]> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/soc_v2/stats/customerdispositions/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Add security_tool parameter if provided
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    return this.http.get<CustomerDispositionData[]>(baseUrl, { params });
  }

  getMttrLineGraphData(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null
  ): Observable<MttrLineGraphData[]> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/soc_v2/stats/mttrline/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Add security_tool parameter if provided
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    return this.http.get<MttrLineGraphData[]>(baseUrl, { params });
  }

  getAutomatedResponseUsageData(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null
  ): Observable<AutomatedResponseUsageData[]> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const url = `${environment.apiUrl}${demostr}/v1/analytics/soc_v2/stats/automatedresponseusage/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Add security_tool parameter if provided
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }
    return this.http.get<AutomatedResponseUsageData[]>(url, { params: params });
  }

  getMttdLineGraphData(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null
  ): Observable<MttdLineGraphData[]> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/soc_v2/stats/mttdviewset/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Add security_tool parameter if provided
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    return this.http.get<MttdLineGraphData[]>(baseUrl, { params });
  }

  getTicketClosureOutcomesData(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null
  ): Observable<TicketClosureOutcomesData[]> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/soc_v2/stats/ticketclosureoutcomes/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Add security_tool parameter if provided
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    return this.http.get<TicketClosureOutcomesData[]>(baseUrl, { params });
  }

  getTicketCategoryPerformanceData(
    organizationIds: number | number[],
    current_start: string,
    current_end: string,
    type?: string,
    securityTool?: string | null
  ): Observable<TicketCategoryPerformanceData[]> {
    let demostr = checkIsDemoOrg() ? '/demo' : '';
    const baseUrl = `${environment.apiUrl}${demostr}/v1/analytics/soc_v2/stats/ticketcategoryperformance/`;
    const orgParam = Array.isArray(organizationIds) ? organizationIds.join(',') : organizationIds.toString();

    const determinedType = Array.isArray(organizationIds) && organizationIds.length > 0 ? 'filtered' : 'all';
    const typeToUse = type || determinedType;

    let params = new HttpParams()
      .set('current_start', current_start)
      .set('current_end', current_end)
      .set('org_ids', orgParam)
      .set('type', typeToUse);

    // Add security_tool parameter if provided
    if (securityTool) {
      params = params.set('security_tool', securityTool);
    }

    return this.http.get<TicketCategoryPerformanceData[]>(baseUrl, { params });
  }
}

export class AlertStatSummaryResponse {
  total_ingest_current = 0;
  total_ingest_previous = 0;
  unique_case_count_current = 0;
  unique_case_count_previous = 0;
  total_ingest_pct_change = 0;
  unique_case_count_pct_change = 0;
}

export class ticketTrendsResponse {
  total_count_current = 0;
  total_count_previous = 0;
  total_tp_count_current = 0;
  total_tp_count_previous = 0;
  total_count_pct_change = 0;
  total_tp_count_pct_change = 0;
}

export class SlaAdherenceResponse {
  sla_violation_count_current = 0;
  sla_violation_count_previous = 0;
  sla_violation_pct_change = 0;
  sla_adherence_pct_current = 0;
  sla_adherence_pct_previous = 0;
  sla_adherence_pct_change = 0;
}

export class PartnerMttdResponse {
  average_mttd_minutes_current = 0;
  average_mttd_minutes_previous = 0;
  average_mttd_minutes_pct_change = 0;
}

export class AverageMttrResponse {
  average_mttr_hours_current = 0;
  average_mttr_hours_previous = 0;
  average_mttr_hours_pct_change = 0;
}

export class PartnerOrgCountResponse {
  org_count_current = 0;
  org_count_previous = 0;
  org_count_pct_change = 0;
}
export interface WidgetData {
  value: number;
  colors: {
    to: number;
    color: string;
    from: number;
  }[];
  scale?: {
    rangeSize?: number;
    rangeDistance?: number;
    majorUnit?: number;
    minorUnit?: number;
  };
}

export interface CoverageData {
  value: CoverageItem[];
}

export interface CoverageItem {
  name: string;
  value: number;
}

export interface MapData {
  region: string;
  count: number;
  location: [number, number];
}

export interface DispositionData {
  closure_reason: string;
  count: number;
}

export interface TopFiveData {
  description: string;
  count: number;
}

export interface SeriesData {
  name: string;
  data: number[];
}

export interface AdrManualData {
  category: string;
  value: number;
}

export interface TrendingData {
  title: string;
  categories: string[];
  series: SeriesData[];
}

export interface FunnelData {
  stat: string;
  count: number;
}

export interface MttrData {
  description: string;
  count: string;
}

export interface MttdData {
  description: string;
  count: string;
}

export interface ConnectorStatusData {
  connector_name: string;
  duration: string;
}

export interface ChartData {
  description: string;
  count: number;
  color: string;
}

export class SocEvent {
  id: number | null = null;
  case_id: number | null = null;
  case_created_on: Date | null = null;
  event_name: string | null = null;
  case_severity: string | null = null;
  zoho_ticket_number: string | null = null;
  case_source: string | null = null;
  sla_exceeded: string | null = null;
  alert_received_on: string | null = null;
  case_closed_on: string | null = null;
  closure_reason: string | null = null;
  mitre_tactic: string | null = null;
  ticketed: boolean | null = false;
  event_occurred_on: Date | null = null;
  close_reason_justification: string | null = null;
  tracking_id: string | null = null;
  mttd: Date | null = null;
  triage_type: string | null = null;
}

export interface UnknownRateCustomer {
  customer: string;
  total_tickets: number;
  unknown_tickets: number;
  customer_unknown_rate_percent: number;
  contribution_to_total_unknowns_percent: number;
}

export interface TopUnknownRateInfo {
  total_unknown_count: number;
  total_tickets_count: number;
  total_unknown_percentage: number;
}

export interface TopUnknownRateCustomer {
  ticket_info: TopUnknownRateInfo;
  data: UnknownRateCustomer;
}

export interface TicketsDispositionData {
  disposition: string;
  count: number;
  percentage: number;
}

export interface CustomerDispositionData {
  disposition: string;
  count: number;
  percentage: number;
}

export interface TopMttdDetectionsData {
  detection_name: string;
  mttd: number;
}

export interface AutomatedResponseUsageData {
  use_case: string;
  count: number;
  description: string;
}

export interface MttdLineGraphData {
  date: string;
  total_mttd: number;
}
export interface MttrLineGraphData {
  date: string;
  total_mttr_minutes: number;
  mttr_minutes_with_response: number;
}

export interface TicketCategoryPerformanceData {
  category: string;
  count: number;
  average_mttr_minutes: number;
}

export interface TicketClosureOutcomesData {
  period: string;
  false_positive_count: number;
  unknown_count: number;
  true_positive_count: number;
  total_count: number;
}
